import { compose, lifecycle, withProps, withState } from 'recompose'

import { REFERRAL_PAYLOAD_STORAGE_KEY } from 'app-constants'
import { withBackend } from 'utils/backend'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { isReferralCodeValid } from 'utils/referral'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

type RewardAmountProps = {
  referree: number
  referrer: number
}

type ReferralProps = {
  code: string
  first_name?: string
  group: string
  last_name?: string
  reward_amounts: RewardAmountProps
}

type BackendProps = {
  backend: any
}

type IProps = {
  referral: ReferralProps
}

type Props = BackendProps & EnvironmentProps & WithOptimizelyProps

export type ReferralProp = IProps

export default compose<IProps, {}>(
  withBackend,
  withEnv,
  withOptimizely,
  withState(
    'referralCode',
    'setReferralCode',
    removeDuplicateQueryParams(location.search).referral_code || null,
  ),
  withState('s1', 'setS1', removeDuplicateQueryParams(location.search).s1 || null),
  withState(
    'codeGroup',
    'setCodeGroup',
    removeDuplicateQueryParams(location.search).code_group || 'acorns',
  ),
  lifecycle<Props, {}>({
    componentDidMount() {
      // Check if query string contains referral code
      const queryString = removeDuplicateQueryParams(location.search)

      const referralCode = queryString.referral_code
      const s1 = queryString.s1

      if (referralCode) {
        const code = referralCode.toUpperCase()
        const group = queryString.code_group || 'acorns'
        const url = `web/v3/invitations/${code}?code_group=${group}`

        // NOTE: The need to check for Referrals as s1 is because only user-to-user
        // referrals will be handled by Extole. The plan is to eventually remove this
        // check when all referrals (e.g. partner referrals, sign on bonuses) utilize
        // Extole to fulfill those rewards.
        if (s1 === 'Referrals') {
          isReferralCodeValid(this.props.env, referralCode).catch(error => {
            sendToSentry(error, {
              name: 'ExtoleReferralCodeError',
              extras: { code, error },
            })
          })
        } else {
          this.props
            .backend(url)
            .then(result => {
              const referralData = {
                group,
                ...result.data.invitation,
              }

              window.sessionStorage.setItem(
                REFERRAL_PAYLOAD_STORAGE_KEY,
                JSON.stringify(referralData),
              )
            })
            .catch(error => {
              const { response } = error

              if (response?.status === 404) {
                sendToSentry(error, {
                  name: 'v3/invitations 404',
                  extras: {
                    code,
                    codeGroup: group,
                    error: response.data?.errors?.[0] || null,
                  },
                })
              } else {
                sendToSentry(error, {
                  name: 'NewReferralCodeError',
                  message: 'An error occurred while attempting to retrieve referral data',
                  extras: { code, error },
                })
              }
            })
        }
      }
    },
  }),
  withProps(({ referralCode, s1, codeGroup }) => {
    let referralData = JSON.parse(window.sessionStorage.getItem(REFERRAL_PAYLOAD_STORAGE_KEY))

    // INCT-263 - https://acorns.atlassian.net/browse/INCT-263
    // We are doing this because if a user is passed to registration with a referral_code in
    // their query string and we are unable to resolve their referral_code at v3/invitations, we want
    // to still pass back the attempted code and group to be passed as attributes in the input
    // to createUser.
    if (!referralData && referralCode) {
      referralData = {
        code: referralCode,
        reward_amounts: {
          referrer: null,
          referree: null,
        },
        group: codeGroup,
        s1,
      }
    }
    return {
      referral: referralData,
    }
  }),
)
